import { Type } from 'class-transformer';
import {
  ArrayMaxSize,
  ArrayMinSize,
  IsArray,
  IsDateString,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  ValidateNested,
} from 'class-validator';
import { BaseDto } from '../../@base/base.dto';
import { AppointmentId, DiscussionId, PropertyApplicationId, PropertyId, UserId } from '../../types';

export enum AppointmentStatus {
  PROPOSED = 'proposed',
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
  RESCHEDULED = 'rescheduled',
  COMPLETED = 'completed',
  NO_SHOW = 'no_show',
}

export enum AppointmentType {
  PROPERTY_VISIT = 'property_visit',
  MEETING = 'meeting',
  GENERAL = 'general',
}

export enum AppointmentSlotStatus {
  PROPOSED = 'proposed',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
}

export class AppointmentSlotDto {
  @IsString()
  @IsOptional()
  _id?: string;

  @IsDateString()
  @IsNotEmpty()
  startAt!: string;

  @IsDateString()
  @IsNotEmpty()
  endAt!: string;

  @IsEnum(AppointmentSlotStatus)
  @IsNotEmpty()
  status!: AppointmentSlotStatus;

  @IsString()
  @IsOptional()
  declineReason?: string;
}

export class AppointmentAttendeeDto {
  @IsString()
  @IsNotEmpty()
  user!: UserId;

  @IsString()
  @IsNotEmpty()
  role!: 'organizer' | 'attendee';

  @IsString()
  @IsNotEmpty()
  status!: 'pending' | 'accepted' | 'declined';

  @IsString()
  @IsOptional()
  name?: string;

  @IsString()
  @IsOptional()
  avatarUrl?: string;
}

export class AppointmentLocationDto {
  @IsString()
  @IsNotEmpty()
  type!: 'physical' | 'virtual' | 'phone';

  @IsString()
  @IsOptional()
  address?: string;

  @IsString()
  @IsOptional()
  details?: string;

  @IsString()
  @IsOptional()
  link?: string;
}

export class AppointmentContextDto {
  @IsString()
  @IsNotEmpty()
  type!: 'property_visit' | 'general' | string;

  @IsString()
  @IsOptional()
  property?: PropertyId;

  @IsString()
  @IsOptional()
  application?: PropertyApplicationId;

  @IsString()
  @IsOptional()
  discussion?: DiscussionId;

  @IsString()
  @IsOptional()
  propertyTitle?: string;

  @IsString()
  @IsOptional()
  propertyAddress?: string;

  @IsString()
  @IsOptional()
  propertyCity?: string;

  @IsString()
  @IsOptional()
  propertyType?: string;

  @IsNumber()
  @IsOptional()
  propertySurfaceHabitable?: number;

  @IsNumber()
  @IsOptional()
  propertyPriceCents?: number;

  @IsNumber()
  @IsOptional()
  propertyRoomsCount?: number;

  @IsNumber()
  @IsOptional()
  propertyBedroomsCount?: number;

  @IsString()
  @IsOptional()
  propertyCoverPhotoUrl?: string;
}

export class AppointmentDto extends BaseDto<AppointmentId> {
  @IsString()
  @IsNotEmpty()
  organizer!: UserId;

  @IsString()
  @IsOptional()
  organizerName?: string;

  @IsString()
  @IsOptional()
  organizerAvatarUrl?: string;

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => AppointmentAttendeeDto)
  attendees!: AppointmentAttendeeDto[];

  @IsEnum(AppointmentType)
  @IsNotEmpty()
  type!: AppointmentType;

  @IsEnum(AppointmentStatus)
  @IsNotEmpty()
  status!: AppointmentStatus;

  @IsString()
  @IsNotEmpty()
  timezone!: string;

  @IsArray()
  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => AppointmentSlotDto)
  proposedSlots?: AppointmentSlotDto[];

  @ValidateNested()
  @Type(() => AppointmentSlotDto)
  @IsOptional()
  confirmedSlot?: AppointmentSlotDto;

  @ValidateNested()
  @Type(() => AppointmentLocationDto)
  @IsOptional()
  location?: AppointmentLocationDto;

  @ValidateNested()
  @Type(() => AppointmentContextDto)
  @IsOptional()
  context?: AppointmentContextDto;

  @IsString()
  @IsOptional()
  notes?: string;

  @IsString()
  @IsOptional()
  cancellationReason?: string;
}

export class CreateAppointmentProposalDto {
  @IsString()
  @IsNotEmpty()
  attendee!: UserId;

  @IsEnum(AppointmentType)
  @IsNotEmpty()
  type!: AppointmentType;

  @IsString()
  @IsNotEmpty()
  timezone!: string;

  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(3)
  @ValidateNested({ each: true })
  @Type(() => AppointmentSlotDto)
  proposedSlots!: AppointmentSlotDto[];

  @ValidateNested()
  @Type(() => AppointmentLocationDto)
  @IsOptional()
  location?: AppointmentLocationDto;

  @ValidateNested()
  @Type(() => AppointmentContextDto)
  @IsOptional()
  context?: AppointmentContextDto;

  @IsString()
  @IsOptional()
  notes?: string;
}

export class ConfirmAppointmentSlotDto {
  @IsString()
  @IsNotEmpty()
  slotId!: string;

  @IsString()
  @IsOptional()
  notes?: string;
}

export class ProposeDiscussionVisitDto {
  @IsArray()
  @ArrayMinSize(3)
  @ArrayMaxSize(3)
  @ValidateNested({ each: true })
  @Type(() => AppointmentSlotDto)
  proposedSlots!: AppointmentSlotDto[];

  @IsString()
  @IsOptional()
  notes?: string;
}

export class RequestRescheduleAppointmentDto {
  @IsString()
  @IsOptional()
  reason?: string;
}

export class ReportAppointmentNoShowDto {
  @IsString()
  @IsOptional()
  reason?: string;
}
